import React, { useState } from "react";
import { Button, Form, FormControl, Spinner, Table } from "react-bootstrap";
import { useDispatch } from "react-redux";
import moment from "moment";
import {
  findStudentFees,
  removeFees,
  submitFees,
} from "../../../store/actions/councilActions/addFees";
import {
  courseType,
  examType,
  feesMode,
  getSemester,
  getYears,
} from "../../../utils/constant";
import Loader from "../../Alert/Loader";

const AddStudentFees = () => {
  const dispatch = useDispatch();

  const [roll_number, setroll_number] = useState("");
  const [loading, setLoading] = useState(null);
  const [data, setData] = useState(null);
  const [paidSems, setPaidSems] = useState([]);
  const [loader, setloader] = useState(false);
  // Which fee the council is collecting right now: registration / semester / exam.
  // Only the picked fee's amount is posted into its own column.
  const [feeType, setFeeType] = useState("registration");

  // How this fee was taken. These end up on the fee slip, which has to say
  // when the money came in, how it was paid and who took it.
  const [payDetails, setPayDetails] = useState({
    paid_at: moment().format("YYYY-MM-DDTHH:mm"),
    payment_mode: "cash",
    trx_id: "",
    deposited_by: "admin",
  });

  const setPay = (field) => (e) =>
    setPayDetails({ ...payDetails, [field]: e.target.value });

  const fetchStudent = async () => {
    setLoading(true);
    const response = await dispatch(findStudentFees({ roll_number }));
    if (response) {
      setLoading(false);
      setData(response.data);
      const list = [];
      response.data?.fees?.forEach((fee) => {
        return list.push(+fee.semester);
      });
      setPaidSems(list);
      // One-time courses only have registration/exam; others default to the
      // existing per-semester flow.
      const courseWise =
        response.data?.exam_type === examType.course_wise;
      setFeeType(courseWise ? "registration" : "semester");
    } else {
      setLoading(false);
      setData(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    fetchStudent();
  };

  /*
   * Undo a fee submitted by mistake. The row is removed and the semester goes
   * back to showing a Submit fee button. The server refuses to remove a
   * payment that came through the gateway.
   */
  const handleRemoveFees = async (fee) => {
    const which = fee.semester ? `semester ${fee.semester}` : "this fee";
    if (
      !window.confirm(
        `Remove the ${fee.total_fees} fee for ${which}? It will show as unpaid again.`
      )
    ) {
      return;
    }
    setloader(true);
    const done = await dispatch(removeFees(fee.id));
    setloader(false);
    if (done) fetchStudent();
  };

  const handleSubmitFees = async (postData) => {
    setloader(true);
    const response = await dispatch(
      submitFees({
        ...postData,
        ...payDetails,
        paid_at: moment(payDetails.paid_at).format("YYYY-MM-DD HH:mm:ss"),
      })
    );
    if (response) {
      setloader(false);
      fetchStudent();
    } else {
      setloader(false);
    }
  };

  // A one-time (course_wise) course has no semester division; its chooser
  // offers only Registration and Exam. Keyed ONLY off exam_type (course_type
  // == 1 is not reliable - some yearly courses also use it).
  const isCourseWise = data && data.exam_type === examType.course_wise;

  // Paid fee rows (shared by the Registration/Exam views). The records don't
  // distinguish which fee was paid, so every payment is listed with its Undo.
  const renderPaidHistory = () =>
    data?.fees?.length ? (
      <Table>
        <thead>
          <th>Paid</th>
          <th>Date</th>
          <th>Status</th>
        </thead>
        <tbody>
          {data.fees.map((fee) => (
            <tr key={fee.id}>
              <td>{fee.total_fees}</td>
              <td>{moment(fee.created_at).format("DD/MM/YYYY")}</td>
              <td>
                Paid{" "}
                {!fee.trx_id && (
                  <button
                    onClick={() => handleRemoveFees(fee)}
                    className="btn btn-danger btn-small ml-2"
                  >
                    Undo
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    ) : null;

  return (
    <div>
      <div class="row">
        <Loader active={loader} />
        <div class="col-md-12">
          <div class="BigTitle">Add Student Fees</div>
          <div className="text-center mb-4">
            <Form
              className="d-flex mt-4 col-lg-6 col-md-8"
              onSubmit={handleSubmit}
            >
              <FormControl
                type="search"
                placeholder="Enter Roll Number"
                className="me-2"
                aria-label="Search"
                value={roll_number}
                onChange={(e) => setroll_number(e.target.value)}
              />
              <Button
                type="submit"
                variant="outline-success"
                disabled={!roll_number}
              >
                Search
              </Button>
            </Form>
          </div>
          {loading ? (
            <div className="text-center">
              <Spinner animation="border" role="status" size="lg">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : data === false ? (
            <h3>Record not found</h3>
          ) : data ? (
            <>
              <div className="text-center text-white py-2 bg-secondary">
                {data.student.name}
              </div>
              <div className="card my-3">
                <div className="card-header">Payment details</div>
                <div className="card-body row">
                  <div className="col-md-3 mb-2">
                    <label className="d-block mb-1 font-weight-bold">Fee date &amp; time</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={payDetails.paid_at}
                      onChange={setPay("paid_at")}
                    />
                  </div>
                  <div className="col-md-3 mb-2">
                    <label className="d-block mb-1 font-weight-bold">Payment mode</label>
                    <select
                      className="form-control"
                      value={payDetails.payment_mode}
                      onChange={setPay("payment_mode")}
                    >
                      <option value="cash">Cash</option>
                      <option value="gateway">Payment Gateway</option>
                      <option value="phonepe">PhonePe</option>
                      <option value="paytm">Paytm</option>
                      <option value="gpay">Google Pay</option>
                      <option value="upi">UPI</option>
                      <option value="cheque">Cheque</option>
                      <option value="transfer">Bank Transfer</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="col-md-3 mb-2">
                    <label className="d-block mb-1 font-weight-bold">Transaction ID</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Leave blank for cash"
                      value={payDetails.trx_id}
                      onChange={setPay("trx_id")}
                    />
                  </div>
                  <div className="col-md-3 mb-2">
                    <label className="d-block mb-1 font-weight-bold">Deposited by</label>
                    <select
                      className="form-control"
                      value={payDetails.deposited_by}
                      onChange={setPay("deposited_by")}
                    >
                      <option value="admin">Council (Admin)</option>
                      <option value="center">Center</option>
                      <option value="student">Student</option>
                    </select>
                  </div>
                </div>
              </div>
              {data.student?.feesMode === feesMode.offline ? (
                <div className="row">
                  <div class="col-md-12 col-lg-6">
                    <div className="card">
                      <div className="card-header">{data.name}</div>
                      <Table>
                        <tr>
                          <th>Duration</th>
                          <td>{data.duration}</td>
                        </tr>
                        <tr>
                          <th>Exam Fees</th>
                          <td>{data.exam_fee}rs</td>
                        </tr>
                        <tr>
                          <th>Registration fees</th>
                          <td>{data.registration_fee}rs</td>
                        </tr>
                        <tr>
                          <th>Total fees</th>
                          <td>{+data.registration_fee + +data.exam_fee}rs</td>
                        </tr>
                      </Table>
                    </div>
                  </div>
                  <div class="col-md-12 col-lg-6">
                    <div className="card">
                      <div className="card-header">Fee Status</div>
                      <div className="card-body">
                        <div className="mb-3">
                          <label className="d-block mb-1 font-weight-bold">
                            Fee Type
                          </label>
                          <select
                            className="form-control"
                            value={feeType}
                            onChange={(e) => setFeeType(e.target.value)}
                          >
                            <option value="registration">Registration</option>
                            {!isCourseWise && (
                              <option value="semester">Semester</option>
                            )}
                            <option value="exam">Exam</option>
                          </select>
                        </div>
                        {feeType === "registration" ? (
                          <>
                            <Table>
                              <thead>
                                <th>Registration Fee</th>
                                <th>Date</th>
                                <th>Status</th>
                              </thead>
                              <tbody>
                                <tr>
                                  <td>{data.registration_fee}</td>
                                  <td>--</td>
                                  <td>
                                    <button
                                      onClick={() => {
                                        handleSubmitFees({
                                          roll_number: roll_number,
                                          student_id: data.student.id,
                                          total_fees: data.registration_fee,
                                          registration_fee:
                                            data.registration_fee,
                                          exam_fee: 0,
                                        });
                                      }}
                                      className="btn btn-success btn-small"
                                    >
                                      Submit fee
                                    </button>{" "}
                                  </td>
                                </tr>
                              </tbody>
                            </Table>
                            {renderPaidHistory()}
                          </>
                        ) : feeType === "exam" ? (
                          <>
                            <Table>
                              <thead>
                                <th>Exam Fee</th>
                                <th>Date</th>
                                <th>Status</th>
                              </thead>
                              <tbody>
                                <tr>
                                  <td>{data.exam_fee}</td>
                                  <td>--</td>
                                  <td>
                                    <button
                                      onClick={() => {
                                        handleSubmitFees({
                                          roll_number: roll_number,
                                          student_id: data.student.id,
                                          total_fees: data.exam_fee,
                                          registration_fee: 0,
                                          exam_fee: data.exam_fee,
                                        });
                                      }}
                                      className="btn btn-success btn-small"
                                    >
                                      Submit fee
                                    </button>{" "}
                                  </td>
                                </tr>
                              </tbody>
                            </Table>
                            {renderPaidHistory()}
                          </>
                        ) : data.course_type == courseType.semester ? (
                          <>
                            <Table>
                              <thead>
                                <th>Fees</th>
                                <th>Semester</th>
                                <th>Date</th>
                                <th>Status</th>
                              </thead>
                              <tbody>
                                {data.fees?.map((fee) => (
                                  <tr key={fee.id}>
                                    <td>{fee.total_fees}</td>
                                    <td>{fee.semester}</td>
                                    <td>
                                      {moment(fee.created_at).format(
                                        "DD/MM/YYYY"
                                      )}
                                    </td>
                                    <td>
                                      Paid{" "}
                                      {!fee.trx_id && (
                                        <button
                                          onClick={() => handleRemoveFees(fee)}
                                          className="btn btn-danger btn-small ml-2"
                                        >
                                          Undo
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                                {Array.from(
                                  { length: getSemester(data.duration) },
                                  (_, i) => i + 1
                                )
                                  .filter((x) => !paidSems.includes(x))
                                  ?.map((sem) => (
                                    <tr>
                                      <td>
                                        {+data.registration_fee +
                                          +data.exam_fee}
                                      </td>
                                      <td>{sem}</td>
                                      <td>--</td>
                                      <td>
                                        {" "}
                                        <button
                                          onClick={() => {
                                            handleSubmitFees({
                                              roll_number: roll_number,
                                              student_id: data.student.id,
                                              total_fees:
                                                +data.registration_fee +
                                                +data.exam_fee,
                                              registration_fee:
                                                data.registration_fee,
                                              exam_fee: data.exam_fee,
                                              semester: sem,
                                            });
                                          }}
                                          className="btn btn-success btn-small"
                                        >
                                          Submit fee
                                        </button>{" "}
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </Table>
                          </>
                        ) : (
                          <>
                            <Table>
                              <thead>
                                <th>Fees</th>
                                <th>Year</th>
                                <th>Date</th>
                                <th>Status</th>
                              </thead>

                              {data.fees?.map((fee) => (
                                <tr>
                                  <td>{fee.total_fees}</td>
                                  <td>{fee.semester}</td>
                                  <td>
                                    {moment(data.createdAt).format(
                                      "DD/MM/YYYY"
                                    )}
                                  </td>
                                  <td>Paid</td>
                                </tr>
                              ))}
                              {Array.from(
                                { length: getYears(data.duration) },
                                (_, i) => i + 1
                              )
                                .filter((x) => !paidSems.includes(x))
                                ?.map((sem) => (
                                  <tr>
                                    <td>
                                      {+data.registration_fee + +data.exam_fee}
                                    </td>
                                    <td>{sem}</td>
                                    <td>--</td>
                                    <td>Submit fee</td>
                                  </tr>
                                ))}
                            </Table>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                "Fees to be paid online"
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AddStudentFees;
